/* building-detection.js
   Dachfläche automatisch per Klick erkennen
   Abhängigkeiten: turf.js, mapboxgl, MapboxDraw (alle bereits im Theme geladen)
   Globals aus protectorguardrail.js: map, draw, projectStarted, uprightSpacingM,
     MAP_DETAIL_ZOOM, nextAutoSectionName, mapboxDrawAddCleanFeature,
     updateMeasurements, saveDraftFenceSoon, hideMapClickCard
*/

let buildingDetectionActive = false;
let buildingDetectionAbort = null;

/* ─── Haupt-Einstiegspunkt ────────────────────────────────────────────────── */

async function tryDetectBuildingAtClick(lngLat) {
  if (!lngLat || !projectStarted || !buildingDetectionActive) return false;

  const [lng, lat] = Array.isArray(lngLat)
    ? lngLat
    : [lngLat.lng, lngLat.lat];

  setBuildingDetectionStatus('loading');

  // 1. Mapbox queryRenderedFeatures — sofort, kein API-Call
  let polygon = tryMapboxBuildingAtPoint(lng, lat);

  // 2. Overpass API Fallback (OSM Gebäude-Polygon)
  if (!polygon) {
    if (buildingDetectionAbort) buildingDetectionAbort.abort();
    buildingDetectionAbort = new AbortController();
    try {
      polygon = await fetchBuildingFromOverpass(lat, lng, buildingDetectionAbort.signal);
    } catch (e) {
      if (e.name === 'AbortError') {
        setBuildingDetectionStatus('idle');
        return false;
      }
      console.warn('Building detection error:', e);
    }
  }

  if (!polygon) {
    setBuildingDetectionStatus('not-found');
    return false;
  }

  // Polygon in Mapbox Draw eintragen
  if (draw) {
    draw.deleteAll();
    mapboxDrawAddCleanFeature(polygon.geometry, {
      sectionName: nextAutoSectionName(),
      uprightSpacingM: uprightSpacingM,
    });
  }

  setBuildingDetectionStatus('idle');

  // Erkennungs-Modus nach erfolgreichem Treffer automatisch deaktivieren
  buildingDetectionActive = false;
  const btn = document.getElementById('buildingDetectBtn');
  if (btn) {
    btn.classList.remove('is-active');
    btn.setAttribute('aria-pressed', 'false');
  }

  updateMeasurements();
  saveDraftFenceSoon();

  // Karte auf erkanntes Gebäude zoomen
  try {
    if (typeof turf !== 'undefined') {
      const bb = turf.bbox(polygon);
      if (bb && bb.every(Number.isFinite)) {
        map.fitBounds(
          [[bb[0], bb[1]], [bb[2], bb[3]]],
          { padding: 60, maxZoom: MAP_DETAIL_ZOOM, duration: 600 }
        );
      }
    }
  } catch (_) {}

  return true;
}

/* ─── Mapbox Building-Layer ───────────────────────────────────────────────── */

function tryMapboxBuildingAtPoint(lng, lat) {
  if (!map || typeof turf === 'undefined') return null;

  const point = map.project([lng, lat]);

  // Alle bekannten Mapbox Building-Layer-Namen
  const candidates = [
    'building',
    'building-outline',
    'buildings',
    'building-extrusion',
    'building-footprint',
  ];
  const available = candidates.filter(id => {
    try { return !!map.getLayer(id); } catch (_) { return false; }
  });

  if (!available.length) return null;

  const features = map.queryRenderedFeatures(point, { layers: available });
  if (!features.length) return null;

  const geom = features[0].geometry;
  try {
    if (geom.type === 'Polygon') return turf.polygon(geom.coordinates);
    if (geom.type === 'MultiPolygon') return turf.multiPolygon(geom.coordinates);
  } catch (_) {}

  return null;
}

/* ─── Overpass API (OSM) ──────────────────────────────────────────────────── */

async function fetchBuildingFromOverpass(lat, lng, signal) {
  // around:3 = 3 Meter Radius um den Klickpunkt
  const query = `[out:json][timeout:8];way(around:3,${lat},${lng})["building"];out geom;`;

  let res;
  try {
    res = await fetch('https://overpass-api.de/api/interpreter', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: 'data=' + encodeURIComponent(query),
      signal,
    });
  } catch (e) {
    if (e.name !== 'AbortError') console.warn('Overpass fetch failed:', e);
    return null;
  }

  if (!res.ok) {
    console.warn('Overpass HTTP error:', res.status);
    return null;
  }

  let data;
  try { data = await res.json(); } catch (_) { return null; }

  // Größtes Gebäude nehmen (nach Anzahl Koordinaten als Proxy für Fläche)
  const elements = data?.elements || [];
  if (!elements.length) return null;

  const best = elements
    .filter(el => el.geometry?.length >= 4)
    .sort((a, b) => (b.geometry?.length || 0) - (a.geometry?.length || 0))[0];

  if (!best) return null;

  const coords = best.geometry.map(p => [p.lon, p.lat]);
  // Polygon schließen
  const first = coords[0], last = coords[coords.length - 1];
  if (first && last && (first[0] !== last[0] || first[1] !== last[1])) {
    coords.push([first[0], first[1]]);
  }
  if (coords.length < 4) return null;

  try { return turf.polygon([coords]); } catch (_) { return null; }
}

/* ─── UI ──────────────────────────────────────────────────────────────────── */

function setBuildingDetectionStatus(state) {
  const btn = document.getElementById('buildingDetectBtn');
  const hint = document.getElementById('buildingDetectHint');
  if (!btn) return;

  const states = {
    idle: {
      label: '🏠 Dach erkennen',
      hint: '',
      disabled: false,
    },
    loading: {
      label: '⏳ Erkenne…',
      hint: 'Gebäude wird gesucht…',
      disabled: true,
    },
    'not-found': {
      label: '🏠 Dach erkennen',
      hint: '⚠️ Nicht gefunden – bitte manuell zeichnen',
      disabled: false,
    },
  };

  const s = states[state] || states.idle;
  btn.textContent = s.label;
  btn.disabled = s.disabled;
  btn.style.opacity = s.disabled ? '0.6' : '1';
  if (hint) hint.textContent = s.hint;
}

function toggleBuildingDetection() {
  buildingDetectionActive = !buildingDetectionActive;

  const btn = document.getElementById('buildingDetectBtn');
  const hint = document.getElementById('buildingDetectHint');
  if (!btn) return;

  btn.classList.toggle('is-active', buildingDetectionActive);
  btn.setAttribute('aria-pressed', String(buildingDetectionActive));
  btn.style.background = buildingDetectionActive ? '#1a9e3f' : '#34c759';

  if (hint) {
    hint.textContent = buildingDetectionActive
      ? '👆 Klicke jetzt auf ein Dach auf der Karte'
      : '';
  }
}

function insertBuildingDetectionUI() {
  if (document.getElementById('buildingDetectBtn')) return;

  // Nach der bestehenden preplan-card einfügen
  const anchor = document.querySelector('.preplan-card');
  if (!anchor) return;

  const wrapper = document.createElement('div');
  wrapper.className = 'preplan-card';
  wrapper.style.marginTop = '10px';
  wrapper.innerHTML = `
    <button
      id="buildingDetectBtn"
      type="button"
      aria-pressed="false"
      style="
        width: 100%;
        padding: 10px 14px;
        background: #34c759;
        color: #fff;
        border: none;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        transition: background .15s, opacity .15s;
      "
    >🏠 Dach erkennen</button>
    <p
      id="buildingDetectHint"
      style="
        margin: 6px 0 0;
        font-size: 12px;
        color: #888;
        text-align: center;
        min-height: 16px;
        line-height: 1.4;
      "
    ></p>
  `;
  anchor.after(wrapper);

  document.getElementById('buildingDetectBtn').addEventListener('click', () => {
    if (!projectStarted) {
      const hint = document.getElementById('buildingDetectHint');
      if (hint) hint.textContent = 'Zuerst Adresse suchen → START PROJECT klicken';
      return;
    }
    toggleBuildingDetection();
  });
}

/* ─── Init (von start() aufgerufen) ──────────────────────────────────────── */

function initBuildingDetection() {
  insertBuildingDetectionUI();
}